import type { DisplayLocation } from '@repo/types';
import { Icon, Typography } from '@repo/ui';
import { useEffect, useRef } from 'react';
import useFeature from 'services/features/useFeature';
import { useAnalytics } from '../../contexts/analytics/AnalyticsProvider';
import { useTinyAIPageContext } from '../../hooks/useTinyAIPageContext';
import useImpersonate from '../../services/auth/useImpersonate';
import { useChatbotPrompts } from '../../services/chatbotPrompts/useChatbotPrompts';
import { useTinyAIContext } from './TinyAIContext';

import styles from './TinyAIPromptEntryPoint.module.css';

interface TinyAIPromptEntryPointProps {
  location: DisplayLocation;
  variant: 'inline' | 'banner';
  metricName?: string;
}

export const TinyAIPromptEntryPoint = ({
  location,
  variant,
  metricName,
}: TinyAIPromptEntryPointProps) => {
  const { isFeatureEnabled } = useFeature('tiny_ai_chatbot_in_results_portal');
  const { data: prompts } = useChatbotPrompts(location);
  const { openChat, openChatWithPrompt } = useTinyAIContext();
  const { trackEvent } = useAnalytics();
  const pageContext = useTinyAIPageContext();
  const { impersonateId } = useImpersonate();
  const hasTrackedShown = useRef(false);

  useEffect(() => {
    if (
      !impersonateId &&
      prompts &&
      prompts.length > 0 &&
      !hasTrackedShown.current
    ) {
      hasTrackedShown.current = true;
      trackEvent('TinyAI prompt hook shown', {
        display_location: location,
        prompt_count: prompts.length,
        page_context: pageContext,
      });
    }
  }, [prompts, location, trackEvent, pageContext, impersonateId]);

  if (!isFeatureEnabled || impersonateId || !prompts || prompts.length === 0) {
    return null;
  }

  const handleClick = (prompt: {
    id: number;
    prompt_text: string;
    display_location: string;
  }) => {
    trackEvent('TinyAI prompt hook clicked', {
      prompt_text: prompt.prompt_text,
      display_location: prompt.display_location,
      prompt_id: prompt.id,
      page_context: pageContext,
    });
    openChatWithPrompt({
      text: prompt.prompt_text,
      displayLocation: location,
      promptId: prompt.id,
      pageContext,
    });
  };

  if (variant === 'inline') {
    return (
      <div className={styles.inlineContainer}>
        <div className={styles.inlineDivider} />
        <button className={styles.inlineHeader} onClick={openChat}>
          <Icon name='aiGradient' size='s' preserveColors />
          <Typography variant='eyebrow' className={styles.inlineTitle}>
            Ask AI about {metricName ?? 'this metric'}
          </Typography>
        </button>
        <div className={styles.inlinePrompts}>
          {prompts.map(prompt => (
            <button
              key={prompt.id}
              className={styles.inlinePrompt}
              onClick={() => handleClick(prompt)}
            >
              <Typography variant='caption' className={styles.promptText}>
                {prompt.prompt_text}
              </Typography>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.bannerContainer}>
      <div className={styles.bannerHeader}>
        <Icon name='aiGradient' size='s' preserveColors />
        <Typography variant='heading-s' className={styles.bannerTitle}>
          Ask TinyAI
        </Typography>
      </div>
      <div className={styles.bannerPrompts}>
        {prompts.map(prompt => (
          <button
            key={prompt.id}
            className={styles.bannerPrompt}
            onClick={() => handleClick(prompt)}
          >
            <Typography variant='body-s' className={styles.promptText}>
              {prompt.prompt_text}
            </Typography>
          </button>
        ))}
      </div>
    </div>
  );
};
